import { useApplicationCurrencies, useApplicationBaseCurrency } from "../../../../containers/ApplicationPage/ApplicationViewContext";
import React from "react";
import {
  ApplicationCalculationModel,
  ApplicationCalculationSegmentModel,
  ServiceCostApplicationCalculationModel,
} from "../../../../viewModels/Applications/ApplicationModel";
import { Button, Checkbox, ConfigProvider, Form, message, Select, Table, TableProps, Typography } from "antd";
import getLocalizedName from "../../../../utils/getLocalizedName";
import { useThemeMode } from "../../../../theme/ThemeModeProvider";
import { useAppToken } from "../../../../theme/v6/useAppToken";
import { useSelector } from "react-redux";
import {
  makeSelectApplicationServices,
  makeSelectCompanySettings,
  makeSelectLanguage,
  makeSelectVehicleTypes,
} from "../../../../containers/App/selectors";
import { useTranslation } from "react-i18next";
import LPCustomNumericFormat from "../../../LongpollControls/LPCustomNumericFormat";
import FormUtils from "../../../../utils/FormUtils";
import VatUtils, { WITHOUT_VAT_KEY } from "../../../../utils/VatUtils";
import NumberUtils from "../../../../utils/NumberUtils";
import ClientPriceUtils from "../../../../utils/ClientPriceUtils";
import TablesCellWrapper from "../../../TablesCellWrapper";
import formatPrice from "../../../../utils/formatPrice";
import LPTextArea from "../../../LongpollControls/LPTextArea";
import { UpdateRateFormInput } from "../FtlCalculation";
import { CalculationRowErrorType, CalculationTableErrorType } from "../../types";
import _ from "lodash";
import TableSummary from "./components/TableSummary";
import { DeleteOutlined, EditOutlined, LinkOutlined, PlusOutlined, SplitCellsOutlined } from "@ant-design/icons";
import ApplicationServiceModel from "../../../../viewModels/Applications/ApplicationServiceModel";
import { BitrixCompanyModel } from "../../../../viewModels/Dictionaries/BitrixCompanyModel";
import BitrixCompanySelect from "../../../BitrixCompanySelect";

type FormInput = {
  serviceId?: number;
  count?: number;
  bid?: number;
  contractor?: BitrixCompanyModel;
  currencyId: number;
  rate?: number;
  vat: typeof WITHOUT_VAT_KEY | number;
  vatIncluded: boolean;
  cost?: number;
  costForClient?: number;
  costForClientPerUnit?: number;
  profit?: number;
  serviceDescription?: string;
  comment?: string;
};

export type ServiceTableRow = ServiceCostApplicationCalculationModel & {
  heading: false;
  rowType: "service";
  segmentType?: "DELIVERY" | "BROADCAST";
  isGeneralServices: boolean;
};

type HeadingTableRow = {
  heading: true;
  rowType: "heading";
  isGeneralServices: boolean;
  title: React.ReactNode;
  uuid: string;
};

type CommentTableRow = ServiceCostApplicationCalculationModel & {
  heading: false;
  rowType: "comment";
  service: ServiceTableRow;
};

type DescriptionTableRow = ServiceCostApplicationCalculationModel & {
  heading: false;
  rowType: "description";
  service: ServiceTableRow;
};

export type DataType = ServiceTableRow | HeadingTableRow | CommentTableRow | DescriptionTableRow;

const isHeadingRow = (record: DataType): record is HeadingTableRow => record.rowType === "heading";

const isServiceRow = (record: DataType): record is ServiceTableRow => record.rowType === "service";

const isCommentRow = (record: DataType): record is CommentTableRow => record.rowType === "comment";

const isDescriptionRow = (record: DataType): record is DescriptionTableRow => record.rowType === "description";

const hasServiceComment = (service: ServiceCostApplicationCalculationModel) => Boolean(service.comment?.trim());

const getServiceFromRow = (record: ServiceTableRow): ServiceCostApplicationCalculationModel => {
  const { heading: _heading, rowType: _rowType, segmentType: _segmentType, isGeneralServices: _isGeneralServices, ...service } = record;
  return service;
};

const getCommentRow = (service: ServiceTableRow): CommentTableRow => ({
  ...getServiceFromRow(service),
  heading: false,
  rowType: "comment",
  service,
  uuid: `${service.uuid}-comment`,
});

const getDescriptionRow = (service: ServiceTableRow): DescriptionTableRow => ({
  ...getServiceFromRow(service),
  heading: false,
  rowType: "description",
  service,
  uuid: `${service.uuid}-description`,
});

interface EditableRowProps {
  index: number;
  record: DataType;
  onChange: (service: ServiceCostApplicationCalculationModel, field: keyof FormInput) => void;
}

const EditableRow: React.FC<EditableRowProps> = ({ index: _index, record, onChange, ...props }) => {
  const [form] = Form.useForm();

  const currencies = useApplicationCurrencies();

  React.useEffect(() => {
    if (isServiceRow(record)) {
      const service = getServiceFromRow(record);
      form.setFieldsValue(
        FormUtils.prepareFormValues(
          { ...service, contractor: service.bitrixCompanyInfo },
          {
            transformations: { vat: VatUtils.convertVatValue },
            excludeUndefined: false, // заменяем undefined на null
          },
        ),
      );
    }

    if (isCommentRow(record)) {
      form.setFieldsValue({ comment: record.service.comment });
    }

    if (isDescriptionRow(record)) {
      form.setFieldsValue({ serviceDescription: record.service.serviceDescription });
    }
  }, [record, form]);

  const handleValuesChange = (changedValues: Partial<FormInput>, allValues: FormInput) => {
    if (isDescriptionRow(record)) {
      if ("serviceDescription" in changedValues) {
        onChange({ ...getServiceFromRow(record.service), serviceDescription: allValues.serviceDescription }, "serviceDescription");
      }
      return;
    }

    if (isCommentRow(record)) {
      if ("comment" in changedValues) {
        onChange({ ...getServiceFromRow(record.service), comment: allValues.comment }, "comment");
      }
      return;
    }

    if (isServiceRow(record)) {
      const service = getServiceFromRow(record);
      if ("serviceId" in changedValues) {
        const serviceId = allValues.serviceId;
        onChange({ ...service, serviceId }, "serviceId");
      }

      if ("count" in changedValues) {
        const count = allValues.count;
        const bid = allValues.bid;

        const cost =
          NumberUtils.isNumber(bid) && NumberUtils.isNumber(count)
            ? VatUtils.calculateTotalSumWithVat({
                count,
                bid,
                vat: VatUtils.convertVatValueToUpdate(allValues.vat),
                vatIncluded: allValues.vatIncluded,
              })
            : undefined;
        const clientValues = ClientPriceUtils.fromUnitPrice(allValues.costForClientPerUnit, count, cost);
        onChange({ ...service, count, cost, ...clientValues, clientPriceSource: "UNIT_PRICE" }, "count");
      }

      if ("bid" in changedValues) {
        const bid = allValues.bid;
        const count = allValues.count;
        const cost =
          NumberUtils.isNumber(bid) && NumberUtils.isNumber(count)
            ? VatUtils.calculateTotalSumWithVat({
                count,
                bid,
                vat: VatUtils.convertVatValueToUpdate(allValues.vat),
                vatIncluded: allValues.vatIncluded,
              })
            : undefined;
        onChange(
          {
            ...service,
            bid,
            cost,
            profit: ClientPriceUtils.profitFromAmount(allValues.costForClient, cost),
            clientPriceSource: "UNIT_PRICE",
          },
          "bid",
        );
      }

      if ("contractor" in changedValues) {
        onChange(
          {
            ...service,
            contractor: changedValues.contractor?.name,
            contractorId: changedValues.contractor?.id,
            bitrixCompanyInfo: changedValues.contractor,
          },
          "contractor",
        );
      }

      if ("currencyId" in changedValues) {
        const currencyId = allValues.currencyId;
        const currency = currencies.find((x) => x.id === currencyId)!;
        onChange({ ...service, currencyId, rate: currency.rate }, "currencyId");
      }

      if ("rate" in changedValues) {
        const rate = allValues.rate;
        onChange({ ...service, rate }, "rate");
      }

      if ("vat" in changedValues) {
        const vat = allValues.vat;
        const bid = allValues.bid;
        const count = allValues.count;
        const cost =
          NumberUtils.isNumber(bid) && NumberUtils.isNumber(count)
            ? VatUtils.calculateTotalSumWithVat({
                count,
                bid,
                vat: VatUtils.convertVatValueToUpdate(allValues.vat),
                vatIncluded: allValues.vatIncluded,
              })
            : undefined;
        onChange(
          {
            ...service,
            vat: VatUtils.convertVatValueToUpdate(vat),
            cost,
            profit: ClientPriceUtils.profitFromAmount(allValues.costForClient, cost),
            clientPriceSource: "UNIT_PRICE",
          },
          "vat",
        );
      }

      if ("vatIncluded" in changedValues) {
        const bid = allValues.bid;
        const count = allValues.count;
        const vatIncluded = allValues.vatIncluded;
        const cost =
          NumberUtils.isNumber(bid) && NumberUtils.isNumber(count)
            ? VatUtils.calculateTotalSumWithVat({
                count,
                bid,
                vat: VatUtils.convertVatValueToUpdate(allValues.vat),
                vatIncluded: allValues.vatIncluded,
              })
            : undefined;
        onChange(
          {
            ...service,
            vatIncluded,
            cost,
            profit: ClientPriceUtils.profitFromAmount(allValues.costForClient, cost),
            clientPriceSource: "UNIT_PRICE",
          },
          "vatIncluded",
        );
      }

      if ("cost" in changedValues) {
        const count = allValues.count;
        const cost = allValues.cost;
        const bid = NumberUtils.isNumber(count) && NumberUtils.isNumber(cost) ? NumberUtils.roundTo2Digits(cost / count) : undefined;
        onChange(
          {
            ...service,
            cost,
            bid,
            profit: ClientPriceUtils.profitFromAmount(allValues.costForClient, cost),
            clientPriceSource: "UNIT_PRICE",
          },
          "cost",
        );
      }

      if ("comment" in changedValues) {
        const comment = allValues.comment;
        onChange({ ...service, comment }, "comment");
      }

      if ("costForClientPerUnit" in changedValues) {
        const clientValues = ClientPriceUtils.fromUnitPrice(allValues.costForClientPerUnit, allValues.count, allValues.cost);
        onChange({ ...service, ...clientValues, clientPriceSource: "UNIT_PRICE" }, "costForClientPerUnit");
      }

      if ("profit" in changedValues) {
        const clientValues = ClientPriceUtils.fromProfit(allValues.profit, allValues.count, allValues.cost, allValues.costForClientPerUnit);
        onChange({ ...service, ...clientValues, clientPriceSource: "PROFIT" }, "profit");
      }
    }
  };

  return (
    <Form form={form} component={false} onValuesChange={handleValuesChange}>
      <tr {...props} />
    </Form>
  );
};

type BaseProps = {
  calculationUuid: string;
  calculationSegments: Array<ApplicationCalculationSegmentModel>;
  services: Array<ServiceCostApplicationCalculationModel>;
  creator: ApplicationCalculationModel["creator"];
};

type EditableOnlyProps = {
  canEditManagerServices: boolean;
  canEditLogisticianServices: boolean;
  onChangeServices: (service: Array<ServiceCostApplicationCalculationModel>) => void;
  onUpdateRate: (model: UpdateRateFormInput) => void;
  errors: Array<CalculationTableErrorType>;
  onChangeErrors: (errors: Array<CalculationTableErrorType>) => void;
};

type ReadonlyProps = BaseProps &
  Partial<Record<keyof EditableOnlyProps, never>> & {
    readonly: true;
  };

type EditableProps = BaseProps &
  EditableOnlyProps & {
    readonly: false;
  };

type Props = ReadonlyProps | EditableProps;

const EMPTY_CALCULATION_ERRORS: Array<CalculationTableErrorType> = [];

const CalculationTable = React.memo((props: Props) => {
  const { calculationUuid, calculationSegments, services, creator, readonly } = props;
  const canEditManagerServices = readonly ? false : props.canEditManagerServices;
  const canEditLogisticianServices = readonly ? false : props.canEditLogisticianServices;
  const canEditAnyServices = canEditManagerServices || canEditLogisticianServices;
  const errors = readonly ? EMPTY_CALCULATION_ERRORS : props.errors;
  const { token } = useAppToken();
  const { themeMode } = useThemeMode();
  const { t } = useTranslation("translations");

  const lang = useSelector(makeSelectLanguage());
  const applicationServices = useSelector(makeSelectApplicationServices());
  const currencies = useApplicationCurrencies();
  const companySettings = useSelector(makeSelectCompanySettings());
  const vehicleTypes = useSelector(makeSelectVehicleTypes());
  const baseCompanyCurrency = useApplicationBaseCurrency();

  const [expandedCommentRowUuids, setExpandedCommentRowUuids] = React.useState<Array<string>>([]);

  const expandedCommentRowUuidSet = React.useMemo(() => new Set(expandedCommentRowUuids), [expandedCommentRowUuids]);

  const canEditService = React.useCallback(
    (service: ServiceCostApplicationCalculationModel) =>
      service.serviceType === "MANAGER" ? canEditManagerServices : canEditLogisticianServices,
    [canEditLogisticianServices, canEditManagerServices],
  );

  const isCommentRowVisible = React.useCallback(
    (service: ServiceCostApplicationCalculationModel) => hasServiceComment(service) || expandedCommentRowUuidSet.has(service.uuid),
    [expandedCommentRowUuidSet],
  );

  const dataSource: Array<DataType> = calculationSegments
    .reduce<Array<DataType>>((acc, x, index) => {
      acc.push({
        heading: true,
        rowType: "heading",
        isGeneralServices: false,
        title:
          x.transportationSegmentType === "DELIVERY" ? (
            <Typography.Text strong>
              {index / 2 + 1}. {getLocalizedName(x.segment.fromLocation, lang)} →{" "}
              <span style={{ color: token.colorPrimary }}>{getLocalizedName(x.segment.toLocation, lang)}</span>,{" "}
              {t("from2").toLocaleLowerCase()} {x.deliveryTimeFrom || "-"} {t("to2").toLocaleLowerCase()} {x.deliveryTimeTo || "-"}{" "}
              {t("days_short")}.
            </Typography.Text>
          ) : (
            <Typography.Text strong>
              {t("overload")}, {t("from2").toLocaleLowerCase()} {x.deliveryTimeFrom || "-"} {t("to2").toLocaleLowerCase()}{" "}
              {x.deliveryTimeTo || "-"} {t("days_short")}.
            </Typography.Text>
          ),
        uuid: window.crypto.randomUUID(),
      });

      const segmentServices = services.filter((s) => s.calculationSegmentId === x.id);

      segmentServices.forEach((segmentService) => {
        const connectedService = services.find((s) => segmentService.id && s.logistianServiceCostCalculationId === segmentService.id);
        const serviceRow: ServiceTableRow = {
          ...segmentService,
          heading: false,
          rowType: "service",
          segmentType: x.transportationSegmentType,
          isGeneralServices: false,
        };

        acc.push(serviceRow);
        if (connectedService) {
          acc.push({ ...connectedService, heading: false, rowType: "service", isGeneralServices: false });
        }
        acc.push(getDescriptionRow(serviceRow));
        if (isCommentRowVisible(serviceRow)) {
          acc.push(getCommentRow(serviceRow));
        }
      });

      return acc;
    }, [])
    .concat([
      {
        heading: true,
        rowType: "heading",
        isGeneralServices: true,
        title: <Typography.Text strong>{t("general_services")}</Typography.Text>,
        uuid: window.crypto.randomUUID(),
      },
    ])
    .concat(
      services
        .filter((x) => !x.calculationSegmentId && !x.logistianServiceCostCalculationId)
        .reduce<Array<DataType>>((acc, s) => {
          const serviceRow: ServiceTableRow = { ...s, heading: false, rowType: "service", isGeneralServices: true };
          acc.push(serviceRow);
          acc.push(getDescriptionRow(serviceRow));
          if (isCommentRowVisible(serviceRow)) {
            acc.push(getCommentRow(serviceRow));
          }
          return acc;
        }, []),
    );

  const handleRemoveRow = React.useCallback(
    (uuid: string) => {
      if (props.readonly) {
        return;
      }
      props.onChangeServices(services.filter((x) => x.uuid !== uuid));
    },
    [props, services],
  );

  const handleAddRow = React.useCallback(() => {
    if (props.readonly) {
      return;
    }
    props.onChangeServices([
      ...services,
      {
        uuid: window.crypto.randomUUID(),
        vatIncluded: false,
        currencyId: baseCompanyCurrency.id,
        rate: baseCompanyCurrency.rate,
        count: 1,
        vat: companySettings.vats[0],
        serviceType: canEditManagerServices ? "MANAGER" : "LOGISTICIAN",
      },
    ]);
  }, [baseCompanyCurrency, canEditManagerServices, companySettings, props, services]);

  // Добавляем услугу к перевозке от менеджера для другой валюты
  const handleSplitRow = React.useCallback(
    (service: ServiceCostApplicationCalculationModel) => {
      if (props.readonly) {
        return;
      }
      props.onChangeServices([
        ...services,
        {
          ...service,
          id: undefined!,
          uuid: window.crypto.randomUUID(),
          bid: undefined,
          calculationSegmentId: undefined,
          calculationVehicleId: undefined,
          wishElementId: undefined,
          rateId: undefined,
          serviceType: "MANAGER",
          logistianServiceCostCalculationId: service.id,
          cost: undefined,
          costForClient: undefined,
          costForClientPerUnit: undefined,
          profit: undefined,
        },
      ]);
    },
    [props, services],
  );

  const columns: TableProps<DataType>["columns"] = [
    {
      render: (_, record) => {
        if (isHeadingRow(record))
          return (
            <TablesCellWrapper align="center" gap={4}>
              {record.isGeneralServices && canEditManagerServices && (
                <Button icon={<PlusOutlined />} type="text" size="small" onClick={handleAddRow} />
              )}
              {record.title}
            </TablesCellWrapper>
          );
      },
      width: 0,
      onCell: (record) => ({
        colSpan: isHeadingRow(record) ? columns?.length : 1,
        style: { height: 1 },
      }),
    },
    {
      render: (_, record) => {
        if (isHeadingRow(record) || isDescriptionRow(record)) return null;

        if (isCommentRow(record)) {
          return (
            <TablesCellWrapper align="center" justify="center">
              <Button
                icon={<DeleteOutlined />}
                type="text"
                size="small"
                disabled={!canEditAnyServices}
                title={t("note")}
                onClick={(event) => {
                  event.stopPropagation();
                  if (canEditAnyServices) {
                    handleClearComment(getServiceFromRow(record.service));
                  }
                }}
                style={{ width: 18, height: 18 }}
              />
            </TablesCellWrapper>
          );
        }

        const actions: Array<React.ReactNode> = [];
        const commentRowOpened = expandedCommentRowUuidSet.has(record.uuid);
        const canUseCommentRow = !record.logistianServiceCostCalculationId;

        if (canEditLogisticianServices && record.serviceType === "LOGISTICIAN") {
          actions.push(
            <Button
              key="show-source-service"
              icon={<LinkOutlined />}
              type="text"
              size="small"
              onClick={() => {
                const element = document.getElementById(record.uuid);
                if (element) {
                  element.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                  element.classList.remove("highlight-flash");
                  element.classList.add("highlight-flash");
                  setTimeout(() => {
                    element.classList.remove("highlight-flash");
                  }, 1000);
                }
              }}
              style={{ width: 18, height: 18 }}
            />,
          );
        }

        const connectedService = services.find((s) => s.logistianServiceCostCalculationId === record.id);
        if (
          canEditManagerServices &&
          record.serviceType === "LOGISTICIAN" &&
          !connectedService &&
          !!record.calculationVehicleId &&
          canEditAnyServices
        ) {
          actions.push(
            <Button
              key="split-service"
              icon={<SplitCellsOutlined />}
              type="text"
              size="small"
              onClick={() => handleSplitRow(getServiceFromRow(record))}
              style={{ width: 18, height: 18 }}
            />,
          );
        }

        if (canEditManagerServices && record.serviceType === "MANAGER") {
          actions.push(
            <Button
              key="delete-manager-service"
              icon={<DeleteOutlined />}
              type="text"
              size="small"
              onClick={() => handleRemoveRow(record.uuid)}
              style={{ width: 18, height: 18 }}
            />,
          );
        }

        return (
          <TablesCellWrapper align="center" justify="center" vertical gap={2}>
            {actions}
            {canEditAnyServices && canUseCommentRow && !hasServiceComment(record) && !commentRowOpened && (
              <Button
                icon={<EditOutlined />}
                type="text"
                size="small"
                title={t("note")}
                onClick={(event) => {
                  event.stopPropagation();
                  handleShowCommentRow(record.uuid);
                }}
                style={{ width: 18, height: 18 }}
              />
            )}
          </TablesCellWrapper>
        );
      },
      width: 44,
      onCell: (record, index) => {
        const nextRow = index === undefined ? undefined : dataSource[index + 1];

        return {
          colSpan: isHeadingRow(record) ? 0 : 1,
          rowSpan: isDescriptionRow(record) ? 0 : isServiceRow(record) && nextRow?.rowType === "description" ? 2 : 1,
          style: { height: 1, verticalAlign: "middle" },
        };
      },
    },
    {
      title: <TablesCellWrapper>{t("service_type")}</TablesCellWrapper>,
      ellipsis: true,
      key: "service_type",
      render: (_, record) => {
        if (isHeadingRow(record)) return null;

        if (isDescriptionRow(record)) {
          const disabled = !canEditService(record.service) && !canEditManagerServices;
          const error = Boolean(errors.find((x) => x.type === "row" && x.rowUuid === record.service.uuid && x.errors.serviceDescription));

          return (
            <TablesCellWrapper $disabled={disabled} $error={error} vertical justify="center">
              <Form.Item name="serviceDescription" noStyle>
                <LPTextArea
                  autoSize={{ minRows: 1 }}
                  disabled={disabled}
                  placeholder={t("service_description")}
                  aria-label={t("service_description")}
                  variant="borderless"
                />
              </Form.Item>
            </TablesCellWrapper>
          );
        }

        if (isCommentRow(record)) {
          return (
            <TablesCellWrapper $disabled={!canEditAnyServices} vertical justify="center">
              <Form.Item name="comment" noStyle>
                <LPTextArea
                  autoSize={{ minRows: 2, maxRows: 2 }}
                  disabled={!canEditAnyServices}
                  placeholder={t("note")}
                  variant="borderless"
                />
              </Form.Item>
            </TablesCellWrapper>
          );
        }

        const disabled = !!record.calculationVehicleId || !canEditService(record);

        const error = Boolean(errors.find((x) => x.type === "row" && x.rowUuid === record.uuid && x.errors.serviceId));

        const calculationSegment = calculationSegments.find((x) => x.id === record.calculationSegmentId);
        const calculationVehicle = calculationSegment
          ? calculationSegment.calculationVehicles.find((x) => x.id === record.calculationVehicleId)
          : undefined;
        const vehicleType = calculationVehicle ? vehicleTypes.find((x) => x.id === calculationVehicle.vehicleTypeId) : undefined;
        const wishElement = calculationVehicle ? calculationVehicle.wishElementList.find((x) => x.id === record.wishElementId) : undefined;

        let serviceFilterPredicate: (service: ApplicationServiceModel) => boolean = () => true;

        if (record.segmentType === "DELIVERY" && !record.calculationVehicleId) {
          serviceFilterPredicate = (s) => s.serviceTypes.includes("SEGMENT") && (s.loadTypes || []).includes("FTL");
        }

        if (record.segmentType === "BROADCAST") {
          serviceFilterPredicate = (s) => s.serviceTypes.includes("BROADCAST") && (s.loadTypes || []).includes("FTL");
        }

        if (record.isGeneralServices) {
          serviceFilterPredicate = (s) => s.serviceTypes.includes("GENERAL") && (s.loadTypes || []).includes("FTL");
        }

        return (
          <TablesCellWrapper $disabled={disabled} $error={error} vertical justify="center">
            <Form.Item name="serviceId" noStyle>
              <Select
                style={{ width: "100%" }}
                options={[...applicationServices]
                  .filter(serviceFilterPredicate)
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((x) => ({ label: getLocalizedName(x, lang), value: x.id }))}
                suffixIcon={null}
                styles={{ popup: { root: { width: 450 } } }}
                showSearch={{ filterOption: (input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase()) }}
                size="small"
                variant="borderless"
                disabled={disabled}
              />
            </Form.Item>
            {vehicleType && (
              <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                {getLocalizedName(vehicleType, lang)}
              </Typography.Text>
            )}
            {wishElement && wishElement.hasSvh && (
              <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                {t("SVH")}
              </Typography.Text>
            )}
            {wishElement && wishElement.hasPrr && (
              <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                {t("PRR")}
              </Typography.Text>
            )}
            {wishElement && wishElement.hasExpedition && (
              <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                {t("forwarding")}
              </Typography.Text>
            )}
            {wishElement && wishElement.hasTransitDeclaration && (
              <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                {t("registration_of_transit_declaration")}
              </Typography.Text>
            )}
            {wishElement && wishElement.hasInsurance && (
              <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                {t("insurance")}
              </Typography.Text>
            )}
            {wishElement && wishElement.hasExportDeclaration && (
              <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                {t("export_declaration")}
              </Typography.Text>
            )}
          </TablesCellWrapper>
        );
      },
      onCell: (record) => {
        if (isHeadingRow(record)) {
          return {
            colSpan: 0,
          };
        }

        if (isCommentRow(record) || isDescriptionRow(record)) {
          return {
            colSpan: Math.max((columns?.length ?? 2) - 2, 1),
            style: { height: 1 },
          };
        }

        const connectedService = services.find((s) => s.logistianServiceCostCalculationId === record.id);
        return {
          rowSpan: connectedService ? 2 : 1,
          colSpan: record.logistianServiceCostCalculationId ? 0 : 1,
          style: { height: 1 },
        };
      },
    },
    {
      title: <TablesCellWrapper>{t("contractor")}</TablesCellWrapper>,
      ellipsis: true,
      key: "contractor",
      render: (_, record) => {
        if (!isServiceRow(record)) return null;

        const disabled = !!record.calculationVehicleId || !canEditService(record);
        const error = false;

        return (
          <TablesCellWrapper $disabled={disabled} $error={error} align="center">
            {readonly ? (
              <span>{record.bitrixCompanyInfo?.name || record.contractor || "-"}</span>
            ) : (
              <Form.Item name="contractor" noStyle>
                <BitrixCompanySelect
                  companyType="CARRIER"
                  allowClear
                  size="small"
                  variant="borderless"
                  disabled={disabled}
                  suffixIcon={null}
                  styles={{ popup: { root: { width: 450 } } }}
                />
              </Form.Item>
            )}
          </TablesCellWrapper>
        );
      },
      onCell: (record) => {
        if (!isServiceRow(record)) {
          return {
            colSpan: 0,
          };
        }

        const connectedService = services.find((s) => s.logistianServiceCostCalculationId === record.id);
        return {
          rowSpan: connectedService ? 2 : 1,
          colSpan: record.logistianServiceCostCalculationId ? 0 : 1,
          style: { height: 1 },
        };
      },
    },
    {
      title: <TablesCellWrapper>{t("count")}</TablesCellWrapper>,
      ellipsis: true,
      key: "count",
      render: (_, record) => {
        if (!isServiceRow(record)) return null;

        const disabled =
          (!!record.calculationVehicleId && canEditManagerServices && !canEditLogisticianServices) || !canEditService(record);

        const error = Boolean(errors.find((x) => x.type === "row" && x.rowUuid === record.uuid && x.errors.count));

        return (
          <TablesCellWrapper $disabled={disabled} $error={error}>
            <Form.Item name="count" noStyle>
              <LPCustomNumericFormat
                allowNegative={false}
                decimalScale={0}
                thousandSeparator={" "}
                disabled={disabled}
                variant="borderless"
              />
            </Form.Item>
          </TablesCellWrapper>
        );
      },
      onCell: (record) => {
        if (!isServiceRow(record)) {
          return {
            colSpan: 0,
          };
        }

        const connectedService = services.find((s) => s.logistianServiceCostCalculationId === record.id);
        return {
          rowSpan: connectedService ? 2 : 1,
          colSpan: record.logistianServiceCostCalculationId ? 0 : 1,
          style: { height: 1 },
        };
      },
      width: 52,
    },
    {
      title: <TablesCellWrapper>{t("bid")}</TablesCellWrapper>,
      ellipsis: true,
      key: "bid",
      render: (_, record) => {
        if (!isServiceRow(record)) return null;

        const disabled = !canEditService(record) || !!record.logistianServiceCostCalculationId;
        const error = Boolean(errors.find((x) => x.type === "row" && x.rowUuid === record.uuid && x.errors.bid));

        return (
          <TablesCellWrapper $disabled={disabled} $error={error}>
            <Form.Item name="bid" noStyle>
              <LPCustomNumericFormat
                allowNegative={false}
                decimalScale={2}
                thousandSeparator={" "}
                disabled={disabled}
                variant="borderless"
              />
            </Form.Item>
          </TablesCellWrapper>
        );
      },
      onCell: (record) => ({
        colSpan: isServiceRow(record) ? 1 : 0,
        style: { height: 1 },
      }),
      width: 64,
    },
    {
      title: <TablesCellWrapper>{t("currency")}</TablesCellWrapper>,
      ellipsis: true,
      key: "currencyId",
      render: (_, record) => {
        if (!isServiceRow(record)) return null;

        const disabled = !canEditService(record);
        const error = false;

        return (
          <TablesCellWrapper $disabled={disabled} $error={error} align="center">
            <Form.Item name="currencyId" noStyle>
              <Select
                style={{ width: "100%" }}
                options={currencies.map((x) => ({ label: x.name, value: x.id }))}
                size="small"
                variant="borderless"
                disabled={disabled}
                suffixIcon={null}
                styles={{ content: { overflow: "visible", textOverflow: "clip", marginInlineEnd: 0 }, popup: { root: { width: 100 } } }}
              />
            </Form.Item>
          </TablesCellWrapper>
        );
      },
      onCell: (record) => ({
        colSpan: isServiceRow(record) ? 1 : 0,
        style: { height: 1 },
      }),
      width: 40,
    },
    {
      title: <TablesCellWrapper>{t("rate")}</TablesCellWrapper>,
      ellipsis: true,
      key: "rate",
      render: (_, record) => {
        if (!isServiceRow(record)) return null;

        const disabled = record.currencyId === baseCompanyCurrency.id || !canEditService(record);
        const error = false;

        return (
          <TablesCellWrapper $disabled={disabled} $error={error}>
            <Form.Item name="rate" noStyle>
              <LPCustomNumericFormat allowNegative={false} thousandSeparator={" "} disabled={disabled} variant="borderless" />
            </Form.Item>
          </TablesCellWrapper>
        );
      },
      onCell: (record) => ({
        colSpan: isServiceRow(record) ? 1 : 0,
        style: { height: 1 },
      }),
      width: 46,
    },
    {
      title: <TablesCellWrapper>{t("vat")}</TablesCellWrapper>,
      ellipsis: true,
      key: "vat",
      render: (_, record) => {
        if (!isServiceRow(record)) return null;

        const disabled = !canEditService(record);
        const error = false;

        return (
          <TablesCellWrapper $disabled={disabled} $error={error} align="center">
            <Form.Item name="vat" noStyle>
              <Select
                style={{ width: "100%" }}
                options={VatUtils.convertVatsToSelectOptions(companySettings.vats)}
                size="small"
                variant="borderless"
                disabled={disabled}
                suffixIcon={null}
                styles={{ popup: { root: { width: 100 } } }}
              />
            </Form.Item>
          </TablesCellWrapper>
        );
      },
      onCell: (record) => ({
        colSpan: isServiceRow(record) ? 1 : 0,
        style: { height: 1 },
      }),
      width: 52,
    },
    {
      title: (
        <TablesCellWrapper>
          <Typography.Text type="secondary" style={{ fontSize: 10 }}>
            {t("in_cost_short")}
          </Typography.Text>
        </TablesCellWrapper>
      ),
      ellipsis: true,
      key: "vatIncluded",
      render: (_, record) => {
        if (!isServiceRow(record)) return null;

        const disabled = !canEditService(record);
        const error = false;

        return (
          <TablesCellWrapper $disabled={disabled} $error={error} justify="center">
            <Form.Item name="vatIncluded" valuePropName="checked" noStyle>
              <Checkbox disabled={disabled} />
            </Form.Item>
          </TablesCellWrapper>
        );
      },
      onCell: (record) => ({
        colSpan: isServiceRow(record) ? 1 : 0,
        style: { height: 1 },
      }),
      width: 40,
    },
    {
      title: <TablesCellWrapper>{t("sum")}</TablesCellWrapper>,
      ellipsis: true,
      key: "sum",
      render: (_, record) => {
        if (!isServiceRow(record)) return null;

        const disabled = true;
        const error = false;

        return (
          <TablesCellWrapper $disabled={disabled} $error={error} vertical justify="center">
            <Form.Item name="cost" noStyle>
              <LPCustomNumericFormat
                allowNegative={false}
                thousandSeparator={" "}
                disabled={disabled}
                variant="borderless"
                decimalScale={2}
              />
            </Form.Item>
            {NumberUtils.isNumber(record.costInBaseCurrency) &&
              record.costInBaseCurrency !== 0 &&
              record.currencyId !== baseCompanyCurrency.id &&
              // Убрать артефакт, когда появляется сумма в базовой валюте
              record.cost !== record.costInBaseCurrency && (
                <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                  {formatPrice(record.costInBaseCurrency, baseCompanyCurrency.symbol)}
                </Typography.Text>
              )}
          </TablesCellWrapper>
        );
      },
      onCell: (record) => ({
        colSpan: isServiceRow(record) ? 1 : 0,
        style: { height: 1 },
      }),
      width: 90,
    },
    {
      title: <TablesCellWrapper>{t("cost_for_client_per_unit")}</TablesCellWrapper>,
      ellipsis: true,
      key: "costForClientPerUnit",
      render: (_, record) => {
        if (!isServiceRow(record)) return null;

        const disabled = !canEditManagerServices;
        const error = Boolean(errors.find((x) => x.type === "row" && x.rowUuid === record.uuid && x.errors.costForClientPerUnit));

        return (
          <TablesCellWrapper $disabled={disabled} $error={error} vertical justify="center">
            <Form.Item name="costForClientPerUnit" noStyle>
              <LPCustomNumericFormat
                allowNegative={false}
                thousandSeparator={" "}
                disabled={disabled}
                variant="borderless"
                decimalScale={4}
              />
            </Form.Item>
          </TablesCellWrapper>
        );
      },
      onCell: (record) => ({
        colSpan: isServiceRow(record) ? 1 : 0,
        style: { height: 1 },
      }),
      width: 100,
    },
    {
      title: <TablesCellWrapper>{t("client_amount")}</TablesCellWrapper>,
      ellipsis: true,
      key: "costForClient",
      render: (_, record) => {
        if (!isServiceRow(record)) return null;

        const disabled = true;
        const error = false;

        return (
          <TablesCellWrapper $disabled={disabled} $error={error} vertical justify="center">
            <Form.Item name="costForClient" noStyle>
              <LPCustomNumericFormat
                allowNegative={false}
                thousandSeparator={" "}
                disabled={disabled}
                variant="borderless"
                decimalScale={2}
              />
            </Form.Item>
            {NumberUtils.isNumber(record.costForClientInBaseCurrency) &&
              record.costForClientInBaseCurrency !== 0 &&
              record.currencyId !== baseCompanyCurrency.id &&
              record.costForClient !== record.costForClientInBaseCurrency && (
                <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                  {formatPrice(record.costForClientInBaseCurrency, baseCompanyCurrency.symbol)}
                </Typography.Text>
              )}
          </TablesCellWrapper>
        );
      },
      onCell: (record) => ({
        colSpan: isServiceRow(record) ? 1 : 0,
        style: { height: 1 },
      }),
      width: 90,
    },
    {
      title: <TablesCellWrapper>{t("profit")}</TablesCellWrapper>,
      ellipsis: true,
      key: "profit",
      render: (_, record) => {
        if (!isServiceRow(record)) return null;

        const disabled = !canEditManagerServices;
        const error = false;

        return (
          <TablesCellWrapper $disabled={disabled} $error={error} vertical justify="center">
            <Form.Item name="profit" noStyle>
              <LPCustomNumericFormat thousandSeparator={" "} disabled={disabled} variant="borderless" decimalScale={2} />
            </Form.Item>
            {NumberUtils.isNumber(record.profitInBaseCurrency) &&
              record.profitInBaseCurrency !== 0 &&
              record.currencyId !== baseCompanyCurrency.id &&
              record.profit !== record.profitInBaseCurrency && (
                <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                  {formatPrice(record.profitInBaseCurrency, baseCompanyCurrency.symbol)}
                </Typography.Text>
              )}
          </TablesCellWrapper>
        );
      },
      onCell: (record) => ({
        colSpan: isServiceRow(record) ? 1 : 0,
        style: { height: 1 },
      }),
      width: 90,
    },
  ];

  const handleRemoveRowError = React.useCallback(
    (rowUuid: string, field: keyof FormInput) => {
      if (errors) {
        const rowIndex = errors.findIndex((x) => x.type === "row" && x.calculationUuid === calculationUuid && x.rowUuid === rowUuid);
        if (rowIndex === -1) {
          return errors;
        }
        const copy = [...errors];

        if (
          field === "serviceId" ||
          field === "count" ||
          field === "bid" ||
          field === "costForClient" ||
          field === "costForClientPerUnit" ||
          field === "rate" ||
          field === "serviceDescription"
        ) {
          const errorCopy = _.cloneDeep(copy[rowIndex]) as CalculationRowErrorType;
          delete errorCopy.errors[field];
          if (_.isEmpty(errorCopy.errors)) {
            copy.splice(rowIndex, 1);
          } else {
            copy[rowIndex] = errorCopy;
          }
        }
        return copy;
      }
      return [];
    },
    [errors, calculationUuid],
  );

  const handleChangeService = React.useCallback(
    (service: ServiceCostApplicationCalculationModel, field: keyof FormInput) => {
      if (props.readonly) {
        return;
      }
      const nextErrors = handleRemoveRowError(service.uuid, field);
      props.onChangeErrors(nextErrors);

      if (!service.calculationVehicleId) {
        props.onChangeServices(services.map((x) => (x.id === service.id ? service : x)));
        return;
      }

      if (
        field === "bid" ||
        field === "count" ||
        field === "cost" ||
        field === "vat" ||
        field === "vatIncluded" ||
        field === "currencyId"
      ) {
        const wishElement = calculationSegments
          .find((x) => x.id === service.calculationSegmentId)
          ?.calculationVehicles.find((x) => x.id === service.calculationVehicleId)
          ?.wishElementList.find((x) => x.rate?.id === service.rateId);

        if (wishElement) {
          if (wishElement.rate) {
            props.onUpdateRate({
              wishElementId: wishElement.id,
              oldRateId: wishElement.rate.id,
              newRateDto: {
                ...wishElement.rate,
                count: NumberUtils.isNumber(service.count) ? service.count : 0,
                rate: NumberUtils.isNumber(service.bid) ? service.bid : 0,
                cost: NumberUtils.isNumber(service.cost) ? service.cost : 0,
                currencyId: service.currencyId,
                exchangeRate: NumberUtils.isNumber(service.rate) ? service.rate : 0,
                vat: service.vat,
                vatIncluded: service.vatIncluded,
              },
            });
          } else {
            message.error("System error: rate not found");
          }
        } else {
          message.error("System error: Wish element not found");
        }
      } else {
        props.onChangeServices(services.map((x) => (x.id === service.id ? service : x)));
      }
    },
    [calculationSegments, handleRemoveRowError, props, services],
  );

  const handleShowCommentRow = React.useCallback((serviceUuid: string) => {
    setExpandedCommentRowUuids((current) => (current.includes(serviceUuid) ? current : [...current, serviceUuid]));
  }, []);

  const handleClearComment = React.useCallback(
    (service: ServiceCostApplicationCalculationModel) => {
      handleChangeService({ ...service, comment: undefined }, "comment");
      setExpandedCommentRowUuids((current) => current.filter((uuid) => uuid !== service.uuid));
    },
    [handleChangeService],
  );

  const components = {
    body: {
      row: EditableRow,
    },
  };

  const renderSummary = React.useCallback(
    (data: readonly DataType[]) => {
      return <TableSummary data={data} creator={creator} calculationSegments={calculationSegments} />;
    },
    [creator, calculationSegments],
  );

  return (
    <ConfigProvider
      select={{ styles: { root: { paddingInline: 0 } } }}
      theme={{
        components: {
          Table: {
            fontSize: 12,
            cellFontSizeSM: 12,
            cellPaddingBlockSM: 0,
            cellPaddingInlineSM: 0,
            ...(themeMode === "light" ? { borderColor: token.appGray2, headerBg: token.appGray1 } : {}),
            colorBgContainer: token.colorBgContainer,
          },
          Typography: { fontSize: 12 },
          Form: { itemMarginBottom: 0 },
          Input: {
            fontSize: 12,
            paddingBlock: 0,
            paddingInline: 0,
            paddingBlockSM: 0,
            paddingInlineSM: 0,
            borderRadius: 0,
            colorTextDisabled: token.colorText,
            colorBgContainerDisabled: "transparent",
            activeShadow: "none",
            activeBorderColor: "transparent",
            lineWidthFocus: 0,
          },
          Select: {
            fontSize: 12,
            optionFontSize: 14,
            activeBorderColor: "transparent",
            activeOutlineColor: "transparent",
            lineWidthFocus: 0,
            controlHeightSM: 22,
            controlPaddingHorizontalSM: 0,
            paddingSM: 0,
            borderRadius: 0,
            colorTextDisabled: token.colorText,
            colorBgContainerDisabled: "transparent",
          },
        },
      }}
      componentSize="small"
    >
      <Table<DataType>
        columns={columns}
        dataSource={dataSource}
        size="small"
        bordered
        pagination={false}
        components={components}
        onRow={(record) => ({ record, onChange: handleChangeService } as any)}
        rowHoverable={false}
        rowKey="uuid"
        summary={renderSummary}
      />
    </ConfigProvider>
  );
});

export default CalculationTable;
